/**
 * Pontes do fluxo de numeração do SNCR entre telas e abas.
 *
 * - `requestSncrNumbering`: qualquer fluxo de finalização (React ou Vuex) que
 *   receba o 422 do backend dispara um evento de window; o
 *   `SncrNumberingHost`, montado uma vez na navbar, abre o diálogo. Assim as
 *   três implementações de "finalizar" não precisam conhecer o diálogo.
 * - Login pendente: a aba de retorno do gov.br é OUTRA aba e não recebe
 *   props — o profissional vai pelo localStorage.
 * - Bloco registrado: a aba de retorno avisa a aba original (BroadcastChannel,
 *   com o evento `storage` como reserva) para o diálogo atualizar o saldo.
 */

import {
  SNCR_RECIPE_TYPES,
  isSncrNotification,
  type SncrError,
  type SncrErrorCode,
  type SncrNumberingRequest,
  type SncrRecipeType,
  type SncrWarning
} from '../types'

export const SNCR_NUMBERING_REQUIRED_EVENT = 'sncr:numbering-required'

const PENDING_KEY = 'sncr:pending-login'
const NOTICE_SNOOZE_KEY = 'sncr:notice-snoozed-until'
// "Agora não" no aviso da transição: sem isso, toda finalização com
// antimicrobiano até 30/10 reabriria o diálogo.
const NOTICE_SNOOZE_MS = 24 * 60 * 60 * 1000
const REGISTERED_KEY = 'sncr:block-registered'
const CHANNEL_NAME = 'sncr'
// O state assinado do SNCR expira em 5 min; folga para o login no gov.br.
const PENDING_TTL_MS = 15 * 60 * 1000

const NUMBERING_CODES: SncrErrorCode[] = ['SNCR_SEM_SALDO', 'SNCR_SEM_CRM']

const asRecipeType = (value: unknown): SncrRecipeType =>
  SNCR_RECIPE_TYPES.includes(value as SncrRecipeType)
    ? (value as SncrRecipeType)
    : 'RET'

/**
 * Extrai o erro de numeração do SNCR de uma rejeição — cobre o AxiosError dos
 * react-apps e o objeto `{ message, response }` do interceptor do Vue.
 */
export function parseSncrNumberingError(err: unknown): SncrError | null {
  const data = (
    err as {
      response?: {
        data?: {
          code?: unknown
          message?: unknown
          recipe_type?: unknown
          can_emit_without_number?: unknown
        }
      }
    }
  )?.response?.data
  const code = data?.code

  if (
    typeof code !== 'string' ||
    !NUMBERING_CODES.includes(code as SncrErrorCode)
  ) {
    return null
  }

  return {
    code: code as SncrErrorCode,
    message: typeof data?.message === 'string' ? data.message : '',
    recipeType: asRecipeType(data?.recipe_type),
    canEmitWithoutNumber: data?.can_emit_without_number === true
  }
}

export function requestSncrNumbering(detail: SncrNumberingRequest): void {
  window.dispatchEvent(
    new CustomEvent<SncrNumberingRequest>(SNCR_NUMBERING_REQUIRED_EVENT, {
      detail
    })
  )
}

/**
 * Atalho para os catches de finalização: abre o diálogo e devolve true se era
 * erro do SNCR. `actions` deixa o diálogo refazer a finalização: emitindo sem
 * número (só quando o backend permite, `canEmitWithoutNumber`) ou de novo
 * depois de obter os números.
 */
export function handleSncrNumberingError(
  err: unknown,
  professionalId?: string | null,
  actions: Pick<SncrNumberingRequest, 'onEmitWithoutNumber' | 'onRetry'> = {}
): boolean {
  const sncr = parseSncrNumberingError(err)
  if (!sncr) return false

  requestSncrNumbering({
    ...sncr,
    professionalId,
    onRetry: actions.onRetry,
    onEmitWithoutNumber: sncr.canEmitWithoutNumber
      ? actions.onEmitWithoutNumber
      : undefined
  })

  return true
}

/**
 * Aviso da resposta de finalização (`sncr_warning` / `sncr_warnings`). A
 * notificação de receita não gerada vem primeiro e sempre aparece: o médico
 * precisa prescrevê-la no talonário. O aviso do período de transição (receita
 * saiu sem número) abre o diálogo em modo aviso, a menos que o médico o tenha
 * dispensado nas últimas 24 h. Devolve true quando abriu.
 */
export function handleSncrWarning(
  response:
    | { sncr_warning?: SncrWarning; sncr_warnings?: SncrWarning[] }
    | null
    | undefined,
  professionalId?: string | null,
  now: number = Date.now()
): boolean {
  const warnings = response?.sncr_warnings?.length
    ? response.sncr_warnings
    : response?.sncr_warning
    ? [response.sncr_warning]
    : []
  const notification = warnings.find(
    warning =>
      warning?.code && isSncrNotification(asRecipeType(warning.recipe_type))
  )
  const warning = notification ?? warnings[0]
  if (!warning?.code || (!notification && isSncrNoticeSnoozed(now)))
    return false

  requestSncrNumbering({
    code: warning.code,
    message: warning.message,
    recipeType: asRecipeType(warning.recipe_type),
    professionalId,
    notice: true,
    persistent: notification !== undefined
  })

  return true
}

export function snoozeSncrNotice(now: number = Date.now()): void {
  try {
    localStorage.setItem(NOTICE_SNOOZE_KEY, String(now + NOTICE_SNOOZE_MS))
  } catch {
    // storage bloqueado: o aviso volta na próxima finalização
  }
}

export function isSncrNoticeSnoozed(now: number = Date.now()): boolean {
  try {
    const until = Number(localStorage.getItem(NOTICE_SNOOZE_KEY))
    return Number.isFinite(until) && until > now
  } catch {
    return false
  }
}

export function savePendingSncrLogin(
  professionalId: string,
  recipeType: SncrRecipeType = 'RET',
  // Notificação: quantos números o médico escolheu pedir (10 a 50).
  quantity: number | null = null
): void {
  try {
    localStorage.setItem(
      PENDING_KEY,
      JSON.stringify({
        professionalId,
        recipeType,
        quantity,
        startedAt: Date.now()
      })
    )
  } catch {
    // storage bloqueado: a aba de retorno cai no usuário logado
  }
}

/** Profissional que iniciou o login; null se ausente ou vencido. Não apaga. */
export function readPendingSncrLogin(now: number = Date.now()): string | null {
  try {
    const raw = localStorage.getItem(PENDING_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as {
      professionalId?: string
      startedAt?: number
    }
    if (!parsed.professionalId || !parsed.startedAt) return null

    return now - parsed.startedAt <= PENDING_TTL_MS
      ? parsed.professionalId
      : null
  } catch {
    return null
  }
}

/** Tipo de receita do bloco pedido no login pendente (RET quando ausente). */
export function readPendingSncrRecipeType(): SncrRecipeType {
  try {
    const raw = localStorage.getItem(PENDING_KEY)

    return asRecipeType(
      raw ? (JSON.parse(raw) as { recipeType?: unknown }).recipeType : null
    )
  } catch {
    return 'RET'
  }
}

/** Quantidade de números de notificação pedida no login pendente (null em RET/RCE). */
export function readPendingSncrQuantity(): number | null {
  try {
    const raw = localStorage.getItem(PENDING_KEY)
    const quantity = raw
      ? (JSON.parse(raw) as { quantity?: unknown }).quantity
      : null

    return typeof quantity === 'number' && Number.isInteger(quantity)
      ? quantity
      : null
  } catch {
    return null
  }
}

export function clearPendingSncrLogin(): void {
  try {
    localStorage.removeItem(PENDING_KEY)
  } catch {
    // nada a fazer
  }
}

/** Id do usuário logado (mesma chave `user` do Vue). */
export function currentPersonId(): string | null {
  try {
    const raw = localStorage.getItem('user')

    return raw ? (JSON.parse(raw) as { id?: string }).id ?? null : null
  } catch {
    return null
  }
}

export function notifySncrBlockRegistered(): void {
  try {
    const channel = new BroadcastChannel(CHANNEL_NAME)
    channel.postMessage({ type: 'block-registered' })
    channel.close()
  } catch {
    // sem BroadcastChannel: o evento storage abaixo cobre
  }
  try {
    localStorage.setItem(REGISTERED_KEY, String(Date.now()))
  } catch {
    // nada a fazer
  }
}

/** Escuta o aviso de bloco registrado vindo de outra aba. Devolve o unsubscribe. */
export function onSncrBlockRegistered(callback: () => void): () => void {
  let channel: BroadcastChannel | null = null
  try {
    channel = new BroadcastChannel(CHANNEL_NAME)
    channel.onmessage = event => {
      if (event?.data?.type === 'block-registered') callback()
    }
  } catch {
    channel = null
  }

  const onStorage = (event: StorageEvent) => {
    if (event.key === REGISTERED_KEY) callback()
  }
  window.addEventListener('storage', onStorage)

  return () => {
    channel?.close()
    window.removeEventListener('storage', onStorage)
  }
}
